import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Block as RejectIcon,
  Restore as RestoreIcon,
  Assignment as AssignIcon,
  TrendingUp as BoostIcon,
  Schedule as ExtendIcon,
  Refresh as ResubmitIcon,
  PersonAdd as ReassignIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import ApiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';

const ListingDetail = ({ type = 'job' }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentAdmin } = useAuth();
  const { formatCurrency } = useCurrency();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Dialog states
  const [actionDialog, setActionDialog] = useState({ 
    open: false, 
    action: '', 
    title: '', 
    requiresRemark: true,
    requiresAssignee: false 
  });
  const [remark, setRemark] = useState('');
  const [assignee, setAssignee] = useState('');
  const [admins, setAdmins] = useState([]);

  const fetchListingDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = type === 'job' 
        ? await ApiService.getJobById(id)
        : await ApiService.getServiceById(id);
      
      setListing(response);
    } catch (error) {
      console.error(`Error fetching ${type} detail:`, error);
      setError(`Failed to load ${type} details. Please try again later.`);
    } finally {
      setLoading(false);
    }
  }, [id, type]);

  useEffect(() => {
    fetchListingDetail();
    if (currentAdmin?.role === 'super_admin' || currentAdmin?.role === 'admin') {
      fetchAdmins();
    }
  }, [fetchListingDetail, currentAdmin?.role]);

  const fetchAdmins = async () => {
    try {
      const response = await ApiService.getAdmins();
      setAdmins(response.admins || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'under_review': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'dropped': return 'default';
      default: return 'default';
    }
  };

  const getAvailableActions = (status) => {
    const actions = [];
    
    switch (status) {
      case 'pending':
  actions.push(
    { key: 'view', label: 'View', icon: <ViewIcon />, color: 'primary' },
    { key: 'assign', label: 'Assign', icon: <AssignIcon />, color: 'secondary', requiresAssignee: true }
  );
  if (currentAdmin?.role === 'super_admin') {
    actions.push(
      { key: 'approve', label: 'Approve', icon: <ApproveIcon />, color: 'success' },
      { key: 'reject', label: 'Reject', icon: <RejectIcon />, color: 'error' }
    );
  }
  break;
        
      case 'under_review':
        actions.push(
          { key: 'reassign', label: 'Reassign', icon: <ReassignIcon />, color: 'info', requiresAssignee: true },
          { key: 'approve', label: 'Approve', icon: <ApproveIcon />, color: 'success' },
          { key: 'reject', label: 'Reject', icon: <RejectIcon />, color: 'error' }
        );
        break;
        
      case 'approved':
        actions.push(
          { key: 'drop', label: 'Drop', icon: <DeleteIcon />, color: 'warning' },
          { key: 'boost', label: 'Boost', icon: <BoostIcon />, color: 'primary' },
          { key: 'extend', label: 'Extend', icon: <ExtendIcon />, color: 'info' }
        );
        break;
        
      case 'rejected':
        actions.push(
          { key: 'revive', label: 'Revive', icon: <RestoreIcon />, color: 'success' },
          { key: 'delete', label: 'Delete', icon: <DeleteIcon />, color: 'error' }
        );
        break;
        
      case 'dropped':
        actions.push(
          { key: 'resubmit', label: 'Resubmit', icon: <ResubmitIcon />, color: 'primary' },
          { key: 'delete', label: 'Delete', icon: <DeleteIcon />, color: 'error' }
        );
        break;
        
      default:
        break;
    }
    
    // Always add edit action if user has permission
    if (hasPermission('edit')) {
      actions.unshift({ key: 'edit', label: 'Edit', icon: <EditIcon />, color: 'primary', requiresRemark: false });
    }
    
    return actions;
  };

  const hasPermission = (action) => {
    if (!currentAdmin) return false;
    
    const role = currentAdmin.role;
    const permissions = currentAdmin.permissions || [];
    
    // Super admin has all permissions
    if (role === 'super_admin') return true;
    
    // Check specific permissions based on action
    switch (action) {
      case 'edit':
      case 'view':
        return ['admin', 'review_officer'].includes(role);
      case 'assign':
      case 'reassign':
        return permissions.includes('assign_reviewers') || ['admin'].includes(role);
      case 'approve':
        return permissions.includes('approve_listings') || ['admin'].includes(role);
      case 'reject':
        return permissions.includes('reject_listings') || ['admin'].includes(role);
      case 'delete':
        return ['admin'].includes(role);
      default:
        return ['admin'].includes(role);
    }
  };

  const handleAction = (action, requiresRemark = true, requiresAssignee = false) => {
    const actionTitles = {
      assign: 'Assign Reviewer',
      reassign: 'Reassign Reviewer',
      approve: 'Approve Listing',
      reject: 'Reject Listing',
      drop: 'Drop Listing',
      boost: 'Boost Listing',
      extend: 'Extend Listing',
      revive: 'Revive Listing',
      resubmit: 'Resubmit for Review',
      delete: 'Delete Listing'
    };

    setActionDialog({ 
      open: true, 
      action, 
      title: actionTitles[action] || 'Perform Action',
      requiresRemark,
      requiresAssignee
    });
    setRemark('');
    setAssignee('');
  };

  const executeAction = async () => {
    if (actionDialog.requiresRemark && !remark.trim()) {
      setSnackbar({ open: true, message: 'Please enter a remark', severity: 'error' });
      return;
    }

    if (actionDialog.requiresAssignee && !assignee) {
      setSnackbar({ open: true, message: 'Please select an assignee', severity: 'error' });
      return;
    }

    try {
      const { action } = actionDialog;
      const apiMethod = type === 'job' ? 'Job' : 'Service';
      
      const payload = {
        remark: remark.trim(),
        assignee: assignee || undefined
      };

      switch (action) {
        case 'assign':
          await ApiService[`assign${apiMethod}`](listing._id, payload);
          break;
        case 'reassign':
          await ApiService[`reassign${apiMethod}`](listing._id, payload);
          break;
        case 'approve':
          // For job approvals, send reviewNotes as a plain string to match backend validation
          if (type === 'job') {
            await ApiService.approveJob(listing._id, remark.trim());
          } else {
            await ApiService[`approve${apiMethod}`](listing._id, payload);
          }
          break;
        case 'reject':
          await ApiService[`reject${apiMethod}`](listing._id, payload);
          break;
        case 'drop':
          await ApiService[`drop${apiMethod}`](listing._id, payload);
          break;
        case 'boost':
          await ApiService[`boost${apiMethod}`](listing._id, payload);
          break;
        case 'extend':
          await ApiService[`extend${apiMethod}`](listing._id, payload);
          break;
        case 'revive':
          await ApiService[`revive${apiMethod}`](listing._id, payload);
          break;
        case 'resubmit':
          await ApiService[`resubmit${apiMethod}`](listing._id, payload);
          break;
        case 'delete':
          await ApiService[`delete${apiMethod}`](listing._id);
          break;
        case 'edit':
          navigate(`/admin/${type}s/${listing._id}/edit`);
          return;
        default:
          break;
      }

      setSnackbar({ 
        open: true, 
        message: `${type.charAt(0).toUpperCase() + type.slice(1)} ${action}ed successfully`, 
        severity: 'success' 
      });
      
      setActionDialog({ open: false, action: '', title: '', requiresRemark: true, requiresAssignee: false });
      fetchListingDetail(); // Refresh listing details
    } catch (error) {
      console.error(`Error ${actionDialog.action}ing ${type}:`, error);
      const msg = error?.message || `Failed to ${actionDialog.action} ${type}`;
      setSnackbar({ open: true, message: msg, severity: 'error' });
      if (msg.toLowerCase().includes('session has expired') || msg.toLowerCase().includes('unauthorized')) {
        navigate('/admin/login');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderListingInfo = () => {
    if (!listing) return null;

    if (type === 'job') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Company</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon color="primary" />
              {listing.companyName || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Location</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              {listing.location?.city && listing.location?.country 
                ? `${listing.location.city}, ${listing.location.country}`
                : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Job Type</Typography>
            <Typography variant="body1">{listing.jobType || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Work Location</Typography>
            <Typography variant="body1">{listing.workLocation || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Experience Level</Typography>
            <Typography variant="body1">{listing.experienceLevel || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Category</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon color="primary" />
              {listing.category || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Salary</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon color="primary" />
              {listing.salary?.min && listing.salary?.max 
                ? `${formatCurrency(listing.salary.min)} - ${formatCurrency(listing.salary.max)}${listing.salary.period ? ` per ${listing.salary.period}` : ''}`
                : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Posted Date</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="primary" />
              {formatDate(listing.createdAt)}
            </Typography>
          </Grid>
          {listing.applicationDeadline && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Application Deadline</Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon color="primary" />
                {formatDate(listing.applicationDeadline)}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {listing.description || 'No description available'}
            </Typography>
          </Grid>
          {(listing.requirements?.skills?.length > 0 || (Array.isArray(listing.skills) && listing.skills.length > 0)) && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Required Skills</Typography>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(listing.requirements?.skills || listing.skills).map((skill, index) => (
                  <Chip key={index} label={skill} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
          {listing.requirements?.education && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Education</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {String(listing.requirements.education).replace('-', ' ')}
              </Typography>
            </Grid>
          )}
          {listing.requirements?.experience && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Experience Required</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {(listing.requirements.experience.min || 0) + ' - ' + (listing.requirements.experience.max || 'N/A') + ' years'}
              </Typography>
            </Grid>
          )}
          {listing.requirements?.languages?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Languages</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {listing.requirements.languages.join(', ')}
              </Typography>
            </Grid>
          )}
          {listing.requirements?.certifications?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Certifications</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {listing.requirements.certifications.join(', ')}
              </Typography>
            </Grid>
          )}
          {typeof listing.requirements === 'string' && listing.requirements.trim() && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Requirements</Typography>
              <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {listing.requirements}
              </Typography>
            </Grid>
          )}
          {listing.benefits && listing.benefits.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Benefits</Typography>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {listing.benefits.map((benefit, index) => (
                  <Chip key={index} label={benefit} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      );
    } else {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Provider</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              {listing.provider?.firstName && listing.provider?.lastName 
                ? `${listing.provider.firstName} ${listing.provider.lastName}`
                : listing.providerName || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Category</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CategoryIcon color="primary" />
              {listing.category || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Service Type</Typography>
            <Typography variant="body1">{listing.serviceType || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Pricing</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MoneyIcon color="primary" />
              {listing.pricing?.type === 'hourly' 
                ? `${listing.pricing.amount || 0}/hr`
                : listing.pricing?.type === 'fixed'
                ? `${listing.pricing.amount || 0} (fixed)`
                : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Location</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              {listing.location?.city && listing.location?.country 
                ? `${listing.location.city}, ${listing.location.country}`
                : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Rating</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon color="primary" />
              {listing.rating?.average || 0}/5
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Posted Date</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="primary" />
              {formatDate(listing.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
              {listing.description || 'No description available'}
            </Typography>
          </Grid>
          {listing.skills && listing.skills.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Skills</Typography>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {listing.skills.map((skill, index) => (
                  <Chip key={index} label={skill} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
          {(Array.isArray(listing.responsibilities) && listing.responsibilities.length > 0) || (typeof listing.responsibilities === 'string' && listing.responsibilities.trim()) ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Responsibilities</Typography>
              {Array.isArray(listing.responsibilities) ? (
                <Box sx={{ mt: 1 }}>
                  {listing.responsibilities.map((r, i) => (
                    <Typography key={i} variant="body2">• {r}</Typography>
                  ))}
                </Box>
              ) : (
                (() => {
                  const text = String(listing.responsibilities || '');
                  const byLine = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
                  const numbered = text.split(/\s*\d+\.\s*/).map(s => s.trim()).filter(Boolean);
                  if (byLine.length > 1) {
                    return (
                      <Box sx={{ mt: 1 }}>
                        {byLine.map((t, i) => (<Typography key={`r-line-${i}`} variant="body2">• {t}</Typography>))}
                      </Box>
                    );
                  }
                  if (numbered.length > 1) {
                    return (
                      <Box sx={{ mt: 1 }}>
                        {numbered.map((t, i) => (<Typography key={`r-num-${i}`} variant="body2">• {t}</Typography>))}
                      </Box>
                    );
                  }
                  return (
                    <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                      {text}
                    </Typography>
                  );
                })()
              )}
            </Grid>
          ) : null}
        </Grid>
      );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!listing) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{type.charAt(0).toUpperCase() + type.slice(1)} not found</Alert>
      </Box>
    );
  }

  const availableActions = getAvailableActions(listing.status).filter(action => 
    hasPermission(action.key)
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(`/admin/${type}s`)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {type.charAt(0).toUpperCase() + type.slice(1)} Details
        </Typography>
        <Chip 
          label={listing.status?.replace('_', ' ').toUpperCase()} 
          color={getStatusColor(listing.status)}
          size="medium"
        />
      </Box>

      <Grid container spacing={3}>
        {/* Main Listing Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {listing.title}
            </Typography>
            
            {renderListingInfo()}
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {listing.description || 'No description available'}
            </Typography>
          </Paper>

          {/* Remarks Section */}
          {listing.remarks && listing.remarks.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Remarks & Audit Trail
              </Typography>
              {listing.remarks.map((remarkItem, index) => (
                <Card key={index} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip 
                        label={remarkItem.action} 
                        size="small" 
                        color={remarkItem.action === 'Approved' ? 'success' : 
                               remarkItem.action === 'Rejected' ? 'error' : 'default'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(remarkItem.timestamp)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Admin:</strong> {remarkItem.by || 'System'}
                    </Typography>
                    <Typography variant="body2">
                      {remarkItem.message}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Paper>
          )}
        </Grid>

        {/* Actions Panel */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Admin Actions
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {availableActions.map((action) => (
                <Button
                  key={action.key}
                  variant="contained"
                  color={action.color}
                  startIcon={action.icon}
                  onClick={() => handleAction(action.key, action.requiresRemark !== false, action.requiresAssignee)}
                  fullWidth
                >
                  {action.label}
                </Button>
              ))}
              
              {availableActions.length === 0 && (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No actions available for current status
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ ...actionDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{actionDialog.title}</DialogTitle>
        <DialogContent>
          {actionDialog.requiresAssignee && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assign to</InputLabel>
              <Select
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                label="Assign to"
              >
                {admins.map((admin) => (
                  <MenuItem key={admin._id} value={admin._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24 }}>
                        {admin.firstName?.[0]}{admin.lastName?.[0]}
                      </Avatar>
                      {admin.firstName} {admin.lastName} ({admin.role})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {actionDialog.requiresRemark && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="Enter your remark or reason for this action..."
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ ...actionDialog, open: false })}>
            Cancel
          </Button>
          <Button onClick={executeAction} variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListingDetail;